import { Injectable } from '@angular/core';

import { ViveProgress } from '../classes/models/vive-progress';
import { ComplianceSteps } from '../interfaces/compliance-steps';
import { ViveProgressStatus } from '../interfaces/vive-progress-status';

export interface ComplianceStepNeedingAttention {
  step: ComplianceSteps;
  status: ViveProgressStatus;
}

/**
 * VPDW-4199: each compliance review step component only ever sees its own
 * `ComplianceProgress`, so every step's Auto-Approve prompt read "All findings have
 * passed" even when a sibling step had failed — a step-scoped result presented as a
 * review-wide all-clear. `ComplianceReviewComponent` is the only place that holds the
 * whole picture (its `ComplianceProgressMap`); this service shares that picture with
 * the step components and owns the approval copy so it is defined exactly once.
 *
 * Provided on `ComplianceReviewComponent` (not `providedIn: 'root'`) so it is scoped to
 * one review session, the same way `SharedDataService` is.
 */
@Injectable()
export class ComplianceStepStatusService {
  // Statuses that mean a step has an open problem a reviewer should know about.
  // `Reviewing` / `Missing` / not-started are the normal mid-review state, so they are
  // deliberately not flagged — flagging them would warn on every step of every review.
  private readonly StatusesNeedingAttention: Set<ViveProgressStatus> = new Set([
    'Failed',
    'Resolving',
    'Review Needed'
  ]);

  // Holds a live reference to the host's `ComplianceProgressMap`. The host only ever
  // `.set()`s entries on that map (it is never reassigned), so a single call keeps this
  // service current as steps are reviewed.
  private StepStatusMap: Map<ComplianceSteps, ViveProgress | null> = new Map();

  public setStepStatuses(
    StepStatusMap: Map<ComplianceSteps, ViveProgress | null>
  ): void {
    this.StepStatusMap = StepStatusMap;
  }

  // Every step other than the one being reviewed that currently has an open problem.
  public getStepsNeedingAttention(
    currentStep: ComplianceSteps
  ): ComplianceStepNeedingAttention[] {
    const StepsNeedingAttention: ComplianceStepNeedingAttention[] = [];

    this.StepStatusMap.forEach((ComplianceProgress, step) => {
      if (step === currentStep || !ComplianceProgress) {
        return;
      }

      const { status } = ComplianceProgress;

      if (status && this.StatusesNeedingAttention.has(status)) {
        StepsNeedingAttention.push({ step, status });
      }
    });

    return StepsNeedingAttention;
  }

  /**
   * The Auto-Approve prompt for a single step. Always names the step it approves, and
   * names the sibling steps still needing attention so it can never read as an overall
   * all-clear.
   *
   * `leadClause` replaces the default "all findings passed" clause for steps that
   * qualify some other way (owner review's Sole Proprietorship case). Pass it without
   * trailing punctuation.
   */
  public buildStepApprovalText(
    currentStep: ComplianceSteps,
    leadClause?: string
  ): string {
    const lead =
      leadClause || `All findings for the ${currentStep} step have passed`;
    const StepsNeedingAttention = this.getStepsNeedingAttention(currentStep);

    if (StepsNeedingAttention.length === 0) {
      return `${lead}. Do you want to approve the ${currentStep} step?`;
    }

    const isSingleStep = StepsNeedingAttention.length === 1;
    const stepList = StepsNeedingAttention.map(
      ({ step, status }) => `${step} (${status.toUpperCase()})`
    ).join(', ');

    return [
      `${lead}, but ${isSingleStep ? 'another step still needs' : 'other steps still need'} attention: ${stepList}.`,
      `Approving this step does not clear ${isSingleStep ? 'it' : 'them'}.`,
      `Do you want to approve the ${currentStep} step?`
    ].join(' ');
  }
}
